// M0 SpikeScene: a 7×9 checker board in fixed world units (CELL_WU per cell) and one highlight sprite.
// All Phaser calls go through phaserCompat.ts. Since M5 it is booted only with ?spike=1 (the capability
// probe); its `update` still drives the game loop so the sim runs, undrawn.
import type { Cell, Layout } from '../../layout/computeLayout.ts';
import { cameraFor } from '../cameraMath.ts';
import {
  addCanvasTexture,
  addFrame,
  addImage,
  imageState,
  type PImage,
  type PScene,
  placeImage,
  type SceneHooks,
  setCamera,
  setOriginTopLeft,
  setVisible,
} from './phaserCompat.ts';
import { drawBoardSheet, SPIKE_BOARD_KEY, SPIKE_TILE } from './spikeArt.ts';

export const SPIKE_COLS = 7;
export const SPIKE_ROWS = 9;

const DEPTH_FLOOR = 0;
const DEPTH_HIGHLIGHT = 50;

export interface SpikeSceneHandle {
  hooks: SceneHooks;
  scene(): PScene | null;
  /** Camera zoom/scroll so world cell (0,0) lands on layout.board (§2.9: zoom = cellCss·dprEff/CELL_WU). */
  fit(layout: Layout, dprEff: number): void;
  highlight(cell: Cell | null): void;
  /** The highlight as the Phaser image shows it (visible + position → cell), not the last request. */
  highlighted(): Cell | null;
}

export function createSpikeScene(
  cellWu: number,
  frameCss: number,
  onUpdate?: (nowMs: number) => void,
): SpikeSceneHandle {
  let sc: PScene | null = null;
  let frameImg: PImage | null = null;
  let hl: PImage | null = null;
  let hlCell: Cell | null = null;
  let lastFit: { layout: Layout; dprEff: number } | null = null;

  const applyFit = (): void => {
    if (!sc || !lastFit) return;
    const { layout, dprEff } = lastFit;
    const cam = cameraFor(layout, dprEff, cellWu);
    setCamera(sc, cam.zoom, cam.scrollX, cam.scrollY);
    if (frameImg) {
      const f = (frameCss * cellWu) / layout.cellCss;
      placeImage(
        frameImg,
        -f,
        -f,
        SPIKE_COLS * cellWu + 2 * f,
        SPIKE_ROWS * cellWu + 2 * f,
        DEPTH_FLOOR - 1,
      );
    }
  };

  /** Shows the requested highlight (also replays a request made before create()). */
  const applyHighlight = (): void => {
    if (!hl) return;
    if (hlCell) {
      placeImage(hl, hlCell.x * cellWu, hlCell.y * cellWu, cellWu, cellWu, DEPTH_HIGHLIGHT);
      setVisible(hl, true);
    } else {
      setVisible(hl, false);
    }
  };

  const hooks: SceneHooks = {
    key: 'spike',
    create(scene) {
      sc = scene;
      const sheet = drawBoardSheet();
      const tex = addCanvasTexture(scene, SPIKE_BOARD_KEY, sheet.canvas);
      if (!tex) throw new Error('addCanvas failed');
      for (const [name, [x, y]] of Object.entries(sheet.frames)) {
        addFrame(tex, name, x, y, SPIKE_TILE, SPIKE_TILE);
      }
      frameImg = addImage(scene, 0, 0, SPIKE_BOARD_KEY, 'frame');
      setOriginTopLeft(frameImg);
      for (let y = 0; y < SPIKE_ROWS; y++) {
        for (let x = 0; x < SPIKE_COLS; x++) {
          const img = addImage(scene, 0, 0, SPIKE_BOARD_KEY, (x + y) % 2 === 0 ? 'light' : 'dark');
          setOriginTopLeft(img);
          placeImage(img, x * cellWu, y * cellWu, cellWu, cellWu, DEPTH_FLOOR);
        }
      }
      hl = addImage(scene, 0, 0, SPIKE_BOARD_KEY, 'hl');
      setOriginTopLeft(hl);
      placeImage(hl, 0, 0, cellWu, cellWu, DEPTH_HIGHLIGHT);
      applyHighlight();
      applyFit();
    },
    update() {
      onUpdate?.(performance.now());
    },
  };

  return {
    hooks,
    scene: () => sc,
    fit(layout, dprEff) {
      lastFit = { layout, dprEff };
      applyFit();
    },
    highlight(cell) {
      hlCell = cell;
      applyHighlight();
    },
    highlighted() {
      if (!hl) return null;
      const s = imageState(hl);
      return s.visible ? { x: Math.floor(s.x / cellWu), y: Math.floor(s.y / cellWu) } : null;
    },
  };
}
